import React, { FunctionComponent, useContext } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'rootReducer';
import { Card } from 'components/Card';
import { EventModel } from 'features/events/EventModel';
import { RecordingModel } from 'features/recordings/RecordingModel';
import {
  upcomingEventSelector,
  liveEventSelector
} from 'features/events/eventsSelectors';
import { latestRecordingsSelector } from 'features/recordings/recordingsSelectors';
import { ChannelDetails } from '../ChannelDetails';
import { SwiperList } from 'components/Swipers/SwiperList';
import { Loader } from 'components/Loader';
import { useSubdomain } from 'hooks/useSubdomain';
import { fetchRecordings } from 'features/recordings/recordingsSlice';
import { useDispatch } from 'react-redux';
import { useOnceEffect } from 'hooks/useOnceEffect';
import { UserAgentContext } from 'features/user-agent/userAgent';

export const ChannelHome: FunctionComponent = () => {
  const channel = useSelector(
    (state: RootState) => state.channel.channelDetails
  );
  const channelIsLoaded = useSelector(
    (state: RootState) => state.channel.isLoaded
  );

  const upcomingEvents: EventModel[] = useSelector(upcomingEventSelector)
    .filter((event) => event.isUpcoming())
    .slice(0, 4);

  const latestRecordings: RecordingModel[] = useSelector(
    latestRecordingsSelector
  );

  const eventsLoading = useSelector(
    (state: RootState) => state.events.isLoading
  );
  const recordingsLoading = useSelector(
    (state: RootState) => state.recordings.isLoading
  );

  const liveEvent: EventModel | null = useSelector(liveEventSelector);
  const subdomain = useSubdomain();
  const dispatch = useDispatch();

  useOnceEffect(function fetchInitialRecordings() {
    dispatch(fetchRecordings(subdomain, 1));
  });

  const { isNativeAppWebview } = useContext(UserAgentContext);

  if (!channelIsLoaded || channel == null) {
    return null;
  }

  if (
    (eventsLoading && upcomingEvents.length === 0) ||
    (recordingsLoading && latestRecordings.length === 0)
  ) {
    return <Loader fillComponent={true} hideOnNative={isNativeAppWebview} />;
  }

  if (
    upcomingEvents.length === 0 &&
    latestRecordings.length === 0 &&
    !liveEvent
  ) {
    return (
      <section className="tw-flex tw-flex-col tw-h-full">
        <ChannelDetails />
      </section>
    );
  }

  const featuredUpcomingEvent = !liveEvent && upcomingEvents.length > 0;
  const featuredRecording =
    !liveEvent && !featuredUpcomingEvent && latestRecordings.length > 0;
  const [, ...featuredUpcomingEventList] = upcomingEvents;
  const [, ...featuredRecordingList] = latestRecordings;
  const liveEventFullScreen =
    !!liveEvent && upcomingEvents.length === 0 && latestRecordings.length === 0;
  const upcomingEventFullScreen =
    !liveEvent && upcomingEvents.length === 1 && latestRecordings.length === 0;
  const recordingFullScreen =
    !liveEvent && upcomingEvents.length === 0 && latestRecordings.length === 1;

  return (
    <section
      className={`tw-overflow-hidden ${
        liveEventFullScreen || upcomingEventFullScreen || recordingFullScreen
          ? 'tw-flex tw-flex-col tw-h-full'
          : ''
      }`}
    >
      {liveEvent && (
        <Card
          id={liveEvent.id}
          url={`/events/${liveEvent.id}`}
          title={`${liveEvent.title}`}
          artwork={liveEvent.artwork}
          artworkMode={liveEvent.artworkMode}
          isFallback={liveEvent.isFallback()}
          isScheduledRecording={liveEvent.isScheduledRecording()}
          isPrivate={liveEvent.isPrivate()}
          isActive={liveEvent.isLive()}
          updatedTimestamp={liveEvent.lastUpdatedAt}
          isFeatured={true}
          isFullScreen={liveEventFullScreen}
          type={'event'}
          color={liveEvent.color}
        />
      )}
      {featuredUpcomingEvent && (
        <Card
          id={upcomingEvents[0].id}
          url={`/events/${upcomingEvents[0].id}`}
          title={`${upcomingEvents[0].title}`}
          artwork={upcomingEvents[0].artwork}
          artworkMode={upcomingEvents[0].artworkMode}
          isPrivate={upcomingEvents[0].isPrivate()}
          isActive={upcomingEvents[0].isLive()}
          isScheduledRecording={upcomingEvents[0].isScheduledRecording()}
          updatedTimestamp={upcomingEvents[0].lastUpdatedAt}
          isFeatured={true}
          isFullScreen={upcomingEventFullScreen}
          type={'event'}
          color={upcomingEvents[0].color}
        />
      )}
      {featuredRecording && (
        <Card
          id={latestRecordings[0].id}
          url={`/recordings/${latestRecordings[0].id}`}
          title={`${latestRecordings[0].title}`}
          artwork={latestRecordings[0].artwork}
          artworkMode={latestRecordings[0].artworkMode}
          isFeatured={true}
          isFullScreen={recordingFullScreen}
          type={'recording'}
          duration={latestRecordings[0].duration}
          color={latestRecordings[0].color}
        />
      )}
      <SwiperList
        title={'Upcoming events'}
        url={'/events'}
        eventData={
          featuredUpcomingEvent ? featuredUpcomingEventList : upcomingEvents
        }
      />
      <SwiperList
        title={'Latest recordings'}
        url={'/recordings'}
        recordingData={
          featuredRecording ? featuredRecordingList : latestRecordings
        }
      />
    </section>
  );
};
